import { FC, useState, useEffect, useCallback, ReactNode } from 'react';
import { SubscriptionState } from '../../../hooks/App/useSubscription';
import { DeliveryDates } from '../../../hooks/SubscriptionDetail/useDeliveryDate';
import { PlanGroupProduct } from '../../../hooks/SubscriptionDetail/useDeliveryPlan';
import { Plan } from '../../../hooks/SubscriptionDetail/useDeliveryPlan';

import { useLogging } from '../../../hooks/common/useLogging';

import { Button } from '../../common/Button';
import { PrefixBadge } from '../../common/PrefixBadge';
import { HorizontalRule } from '../../common/HorizontalRule';
import { NavigationWrapper } from '../../common/NavigationWrapper';
import { TotalPrice } from '../TotalPrice';

import dayjs from 'dayjs';
import style from './index.module.css';
import { Summary } from '../../../hooks/SubscriptionDetail/useSummary';

export const DeliveryInformation: FC<{
  subscription: SubscriptionState
  summary: Summary,
  dates: DeliveryDates
  updateDeliveryDays: Function
  skip: Function
  skipCancel: Function
  pause: Function,
  isAllowSkipCancel: boolean,
  activePlanGroups: PlanGroupProduct[], 
  currentPlan: Plan | undefined,
  loadingAny: boolean
  modifyDeliveryPlan: Function
}> = ({ 
  subscription, summary, dates, updateDeliveryDays, 
  skip, skipCancel, pause, isAllowSkipCancel, activePlanGroups,
  currentPlan, loadingAny, modifyDeliveryPlan 
}) => {

  const {
    nextBillingDate,
    nextDeliveryDate,
    nextDeliveryMinDate,
    nextDeliveryMaxDate,
    enabledDeliveryTimes,
    disableDeliveryWeeks,
    modifyLimitDate,
    isBanningModify
  } = dates;

  // date/time/intervalの入力値
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<Date>();
  useEffect(() => {
    if (!nextDeliveryDate || !enabledDeliveryTimes) return;
    setSelectedDeliveryDate(nextDeliveryDate.toDate());
  }, [nextDeliveryDate, enabledDeliveryTimes]);

  const [selectedDeliveryTime, setSelectedDeliveryTime] = useState<string>("AM");
  useEffect(() => {
    if (!subscription) return;
    setSelectedDeliveryTime(subscription.deliveryTime);
  }, [subscription]);

  const [selectedDeliveryInterval, setSelectedDeliveryInterval] = useState<string>();
  useEffect(() => {
    if (!currentPlan) return;
    setSelectedDeliveryInterval(currentPlan.planId);
  }, [currentPlan]);

  // スキップボタンクリックの関数
  const handleClickSkip = useCallback(() => {
    if (!skip || !skipCancel || !nextDeliveryDate) return;
    if (isAllowSkipCancel) {
      skipCancel(nextDeliveryDate);
    } else {
      skip(nextDeliveryDate);
    }
  }, [skip, skipCancel, nextDeliveryDate, isAllowSkipCancel]);

  // 一時停止ボタンクリックの関数
  const handleClickPause = () => pause();

  // 配送頻度変更ボタンクリックの関数
  const handleClickModifyDeliveryInterval = useCallback(() => {
    if (!selectedDeliveryInterval) return;
    modifyDeliveryPlan(selectedDeliveryInterval);
  }, [selectedDeliveryInterval]);

  useLogging('DeliveryInformation', [
    currentPlan, selectedDeliveryDate, selectedDeliveryTime, selectedDeliveryInterval
  ]);

  return (
    <div className={style.wrapper}>
      <Information
        summary={summary}
        nextDeliveryDate={nextDeliveryDate}
        nextBillingDate={nextBillingDate}
        modifyLimitDate={modifyLimitDate}
        isAllowModifyDays={subscription?.shop.isAllowModifyDays}
        allowChangeDeliveryDays={subscription?.shop.mypageChangeDeliveryDays}
      />
      { isBanningModify && (
        <div style={{
          border: '1px solid var(--appColorMain)',
          padding: '1.25em',
          marginTop: '1em',
          textAlign: 'center',
          borderRadius: 'var(--appRadius)',
          color: 'var(--appColorMain)',
          fontWeight: 700
        }}>
          スキップや一時停止・解約・商品数量の変更は、<br/>
          次回決済日の{subscription?.shop.allowModifyDays || 'x'}日前まで可能です。
        </div>
      )}
      { !isBanningModify && (
        <NavigationWrapper useInInformation={true}>
          { subscription?.shop.showSkip && (
            <Button 
              type={ isAllowSkipCancel ? "primary" : "secondary" }
              onClick={handleClickSkip}
              disabled={loadingAny}>
              { isAllowSkipCancel ? 'スキップ解除' : 'スキップする' }
            </Button>
          )}
          { subscription?.shop.showPause && (
            <Button 
              type="secondary"
              onClick={handleClickPause}
              disabled={loadingAny}>
              一時停止する
            </Button>
          )}
        </NavigationWrapper>
      )}
      { subscription?.shop.mypageChangeDeliveryDays && (
        <div className={style.toggle}>
          <HorizontalRule size="small"/>
          <h3 className={style.subTitle}>お届け日の変更</h3>
        </div>
      )}
    </div>
  );
}

const Information: FC<{
  summary: Summary,
  nextDeliveryDate: dayjs.Dayjs | undefined,
  nextBillingDate: dayjs.Dayjs | undefined,
  modifyLimitDate: dayjs.Dayjs | undefined,
  isAllowModifyDays: boolean | undefined,
  allowChangeDeliveryDays: boolean | undefined
}> = ({ summary, nextDeliveryDate, nextBillingDate, modifyLimitDate, isAllowModifyDays, allowChangeDeliveryDays }) => {

  return (
    <div className={style.row}>
      <div className={style.columnLeft}>
        {/*<Item prefix='お届け予定日'>
          <Date value={nextDeliveryDate}/>
        </Item>
        <Item prefix='時間帯'>
          <span className={style.time}>午前中</span>
        </Item>*/}
        <Item prefix='合計金額'>
          <TotalPrice summary={summary}/>
        </Item>
        { isAllowModifyDays && modifyLimitDate && (
          <Item prefix='変更締切'>
            <Date value={modifyLimitDate} useNotice={true}/>
          </Item>
        )}
        <Item prefix='次回決済日'>
          <Date value={nextBillingDate} useNotice={true}/>
        </Item>
        <Item>
          <div className={style.deliveryNotice}>
            {/*※決済確認後、7日以内にお届けします。*/}
            ※決済日に関わらず、ご注文後、通常毎月22日以降にお届けします。
          </div>
        </Item>
      </div>
      {allowChangeDeliveryDays && (
        <div className={style.columnRight}>
          <Button type="primary" onClick={() => {}}>閉じる</Button>
        </div>
      )}
    </div>
  );
}

const Item: FC<{
  prefix?: string,
  children: ReactNode
}> = ({ prefix, children }) => {

  return (
    <div className={style.item}>
      { prefix && (
        <PrefixBadge>{prefix}</PrefixBadge>
      )}
      <span className={style.item_content}>{ children }</span>
    </div>
  );
}

const Date: FC<{
  value: dayjs.Dayjs | undefined,
  useNotice?: boolean
}> = ({ value, useNotice = false }) => {

  if (!value) return ( <span>読み込み中</span> );

  const year = value.format('YYYY');
  const month = value.format('MM').padStart(2, '0');
  const date = value.format('DD').padStart(2, '0');
  const day = value.format('ddd');
  const time = value.format('HH:mm');

  const className = useNotice ? style.dateNotice : style.date;

  return (
    <span className={className}>
      <strong>{year}</strong>
      <span>年</span>
      <strong>{month}</strong>
      <span>月</span>
      <strong>{date}</strong>
      <span>日</span>
      <i>{day}</i>
      { useNotice && (
        <strong>{time}</strong>
      )}
    </span>
  )
}
